import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import AppLoadingState from '../components/common/AppLoadingState';

const AuthContext = createContext(null);

const AUTH_USER_KEY = 'user';

function readStoredUserRaw() {
  return (
    sessionStorage.getItem(AUTH_USER_KEY) ||
    localStorage.getItem(AUTH_USER_KEY)
  );
}

function clearStoredUser() {
  sessionStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateStoredUser = async () => {
      const storedUser = readStoredUserRaw();
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          api.setToken(userData.token);

          await api.request('/auth/validate');

          setUser(userData);
        } catch (error) {
          console.error('Stored token validation failed:', error);
          clearStoredUser();
          api.setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateStoredUser();
  }, []);

  /**
   * @param {object} userData
   * @param {boolean} [rememberMe=true] — false keeps the session in tab-only storage until the browser is closed
   */
  const login = (userData, rememberMe = true) => {
    clearStoredUser();
    const payload = JSON.stringify(userData);
    if (rememberMe) {
      localStorage.setItem(AUTH_USER_KEY, payload);
    } else {
      sessionStorage.setItem(AUTH_USER_KEY, payload);
    }
    setUser(userData);
    api.setToken(userData.token);
  };

  const logout = () => {
    clearStoredUser();
    setUser(null);
    api.setToken(null);
  };

  if (loading) {
    return <AppLoadingState layout="page" showLabel={false} />;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin: user?.isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 